// What the postventa-v2 screens need from the loading contract beyond
// one QueryBoundary: their skeleton and their error live inside the
// route's own frame (a `.panel`, a 404 branch), and some wait on more
// than one independent query (the record + the recipe). They read the
// contract through useQueryView by hand and compose it here:
//
// - `gateOf(views)` folds several independent views into one: the first
//   error wins (a failure is never hidden behind another query's
//   skeleton), then loading, then disabled; `ready` only when every view
//   has data, with each view's data in order.
// - `SlowNotice` / `RefreshNotices` are the same notices QueryBoundary
//   renders (slow first load; failed or long background refresh over
//   kept data), with the same markup and copy.
import type { ReactNode } from "react";
import type { QueryView } from "../lib/loading/useQueryView";

type AnyView = QueryView<unknown, unknown>;
type DataOf<V> = V extends { kind: "ready"; data: infer T } ? T : never;

export type Gate<D> =
  | { kind: "loading"; visible: boolean; slow: boolean }
  | { kind: "error"; error: unknown; offline: boolean; retry: () => void }
  | { kind: "disabled" }
  | { kind: "ready"; data: D; refreshFailed: boolean; revalidatingHint: boolean };

export function gateOf<const V extends readonly AnyView[]>(
  views: V
): Gate<{ [K in keyof V]: DataOf<V[K]> }> {
  for (const v of views) {
    if (v.kind === "error") return v;
  }
  const loading = views.filter((v) => v.kind === "loading");
  if (loading.length > 0) {
    return {
      kind: "loading",
      visible: loading.some((v) => v.visible),
      slow: loading.some((v) => v.slow),
    };
  }
  if (views.some((v) => v.kind === "disabled" || v.kind === "empty")) return { kind: "disabled" };
  const ready = views as unknown as readonly Extract<AnyView, { kind: "ready" }>[];
  return {
    kind: "ready",
    data: ready.map((v) => v.data) as { [K in keyof V]: DataOf<V[K]> },
    refreshFailed: ready.some((v) => v.refreshFailed),
    revalidatingHint: ready.some((v) => v.revalidatingHint),
  };
}

/** Past SKELETON_SLOW_MS, under the skeleton (never replacing it). */
export function SlowNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="notice-banner notice-banner--action">
      <span>
        <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
        intentando.
      </span>
      <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
        Reintentar
      </button>
    </div>
  );
}

/** Over kept data: a failed refresh, or a background refresh past
 *  REVALIDATING_HINT_MS. Renders nothing otherwise. */
export function RefreshNotices({
  refreshFailed,
  revalidatingHint,
  onRetry,
}: {
  refreshFailed: boolean;
  revalidatingHint: boolean;
  onRetry: () => void;
}): ReactNode {
  return (
    <>
      {refreshFailed && (
        <div className="notice-banner notice-banner--action">
          <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
            Reintentar
          </button>
        </div>
      )}
      {revalidatingHint && <p className="refresh-hint">Actualizando…</p>}
    </>
  );
}
